import React, { useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import bannerImg from '../assets/banner.png';
import DataTransfer from '../components/DataTransfer';
import KofiButton from '../components/KofiButton';

const FEATURES = [
  {
    id: 'army-builder',
    to: '/army-builder',
    icon: '⚔',
    title: 'Army Builder',
    subtitle: 'Forge your force',
    description:
      'Build and validate army lists for every Warhammer: The Old World faction - ' +
      'from Empire and High Elves to Tomb Kings and Warriors of Chaos. ' +
      'Live composition checks enforce Lords, Heroes, Core, Special, and Rare percentage limits as you build. ' +
      'Browse the full unit roster with all equipment options and magic items. ' +
      'Share your list to Discord, WhatsApp, or Best Coast Pairings in one tap - ' +
      'or generate a QR code for opponents to scan on the day.',
    cta: 'Open Army Builder',
    accent: 'var(--f-cat-characters)',
  },
  {
    id: 'adjutant',
    to: '/turn-tracker',
    icon: '🎲',
    title: "General's Adjutant",
    subtitle: 'Command the battlefield',
    description:
      'A full game companion from deployment to final turn. ' +
      'Roll your matched-play scenario from all six options, with accurate deployment diagrams and zone measurements. ' +
      'Pick secondary objectives for both players and track their progress through the game. ' +
      'Manage army setup - wizards, bound spells, and vanguard moves - before the first turn. ' +
      'Scan your opponent\'s Battle Standard QR code to pull their list directly into the game. ' +
      'Then track turns, phases, spell casts, and game events so nothing slips through the cracks.',
    cta: 'Open Adjutant',
    accent: 'var(--f-cat-core)',
  },
];

const legalHeading: React.CSSProperties = {
  fontFamily: "'Cinzel', Georgia, serif",
  fontSize: '8px',
  letterSpacing: '0.4em',
  textTransform: 'uppercase',
  color: 'var(--f-text-4)',
  margin: '0 0 10px',
};

const legalText: React.CSSProperties = {
  fontFamily: "'Source Serif 4', Georgia, serif",
  fontStyle: 'italic',
  fontSize: '11px',
  color: 'var(--f-text-4)',
  lineHeight: 1.75,
  margin: '0 0 10px',
};

/** Triggers a fade-up animation once the element enters the viewport */
function useRevealOnScroll() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.style.animation = 'fade-up 0.7s ease both';
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

export default function Home() {
  const taglineRef = useRevealOnScroll();
  const cardsRef = useRevealOnScroll();
  const loginRef = useRevealOnScroll();
  const supportRef = useRevealOnScroll();
  const footerRef = useRevealOnScroll();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>

      {/* ── Full banner - no crop ── */}
      <img
        src={bannerImg}
        alt="Battle Standard - Warhammer: The Old World companion"
        style={{ width: '100%', height: 'auto', display: 'block' }}
      />

      {/* ── Body ── */}
      <div style={{ flex: 1, backgroundColor: 'var(--f-bg)', padding: '0 16px 64px' }}>

        {/* Tagline card */}
        <div
          ref={taglineRef}
          style={{ opacity: 0, textAlign: 'center', padding: '40px 0 36px' }}
        >
          <p style={{
            fontFamily: "'Cinzel', Georgia, serif",
            fontSize: '9px',
            letterSpacing: '0.5em',
            textTransform: 'uppercase',
            color: 'var(--f-text-4)',
            marginBottom: '10px',
          }}>
            Warhammer: The Old World
          </p>
          <h1 style={{
            fontFamily: "'Cinzel Decorative', 'Cinzel', Georgia, serif",
            fontSize: 'clamp(22px, 4vw, 36px)',
            fontWeight: 700,
            color: 'var(--f-text)',
            letterSpacing: '0.04em',
            margin: '0 0 12px',
            lineHeight: 1.1,
          }}>
            Your Companion to<br />the Battlefield
          </h1>
          <p style={{
            fontFamily: "'Source Serif 4', Georgia, serif",
            fontStyle: 'italic',
            fontSize: '15px',
            color: 'var(--f-text-3)',
            maxWidth: '480px',
            margin: '0 auto',
            lineHeight: 1.6,
          }}>
            Build armies, manage games, and master the rules - all in one offline-first app.
          </p>
          <div className="faction-gold-rule" style={{ maxWidth: '320px', margin: '24px auto 0' }} />
        </div>

        {/* Feature cards */}
        <div
          ref={cardsRef}
          style={{
            opacity: 0,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: '16px',
            maxWidth: '860px',
            margin: '0 auto 40px',
          }}
        >
          {FEATURES.map((f) => (
            <div key={f.id} style={{
              backgroundColor: 'var(--f-card)',
              border: '1px solid var(--f-border)',
              borderTop: `4px solid ${f.accent}`,
              padding: '28px 28px 24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
            }}>
              <div style={{ fontSize: '28px', lineHeight: 1 }}>{f.icon}</div>
              <div>
                <p style={{
                  fontFamily: "'Cinzel', Georgia, serif",
                  fontSize: '8px',
                  letterSpacing: '0.4em',
                  textTransform: 'uppercase',
                  color: 'var(--f-text-4)',
                  margin: '0 0 4px',
                }}>
                  {f.subtitle}
                </p>
                <h2 style={{
                  fontFamily: "'Cinzel', Georgia, serif",
                  fontSize: '20px',
                  fontWeight: 700,
                  color: 'var(--f-text)',
                  margin: 0,
                  letterSpacing: '0.03em',
                }}>
                  {f.title}
                </h2>
              </div>
              <p style={{
                fontFamily: "'Source Serif 4', Georgia, serif",
                fontSize: '14px',
                lineHeight: 1.65,
                color: 'var(--f-text-3)',
                margin: 0,
                flex: 1,
              }}>
                {f.description}
              </p>
              <NavLink
                to={f.to}
                style={{
                  display: 'block',
                  marginTop: '8px',
                  padding: '11px 20px',
                  backgroundColor: 'var(--f-primary)',
                  color: '#fff',
                  fontFamily: "'Cinzel', Georgia, serif",
                  fontSize: '10px',
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                  textDecoration: 'none',
                  textAlign: 'center',
                  transition: 'opacity 0.15s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.85')}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
              >
                {f.cta} →
              </NavLink>
            </div>
          ))}
        </div>

        {/* Data management */}
        <div
          ref={loginRef}
          style={{
            opacity: 0,
            maxWidth: '480px',
            margin: '0 auto 40px',
            backgroundColor: 'var(--f-elevated)',
            border: '1px solid var(--f-border)',
            padding: '28px 32px',
          }}
        >
          {/* Export / Import */}
          <p style={{
            fontFamily: "'Cinzel', Georgia, serif",
            fontSize: '8px',
            letterSpacing: '0.45em',
            textTransform: 'uppercase',
            color: 'var(--f-text-4)',
            marginBottom: '8px',
          }}>
            My Data
          </p>
          <h3 style={{
            fontFamily: "'Cinzel', Georgia, serif",
            fontSize: '17px',
            fontWeight: 700,
            color: 'var(--f-text)',
            margin: '0 0 8px',
          }}>
            Export &amp; Import
          </h3>
          <p style={{
            fontFamily: "'Source Serif 4', Georgia, serif",
            fontStyle: 'italic',
            fontSize: '13px',
            color: 'var(--f-text-3)',
            lineHeight: 1.6,
            margin: '0 0 16px',
          }}>
            Back up your armies and game history, or transfer them to another device. No account required.
          </p>
          <DataTransfer />

          {/* Cloud sync scaffold */}
          <div style={{ marginTop: '24px', paddingTop: '20px', borderTop: '1px solid var(--f-border)', textAlign: 'center' }}>
            <p style={{
              fontFamily: "'Cinzel', Georgia, serif",
              fontSize: '8px',
              letterSpacing: '0.45em',
              textTransform: 'uppercase',
              color: 'var(--f-text-4)',
              marginBottom: '8px',
            }}>
              Your Account
            </p>
            <p style={{
              fontFamily: "'Source Serif 4', Georgia, serif",
              fontStyle: 'italic',
              fontSize: '13px',
              color: 'var(--f-text-3)',
              lineHeight: 1.6,
              margin: '0 0 14px',
            }}>
              Sign in to sync your armies automatically across all devices.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'center', marginBottom: '10px' }}>
              <button disabled style={{
                padding: '9px 22px',
                backgroundColor: 'var(--f-primary)',
                color: '#fff',
                fontFamily: "'Cinzel', Georgia, serif",
                fontSize: '10px',
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                border: 'none',
                cursor: 'not-allowed',
                opacity: 0.45,
              }}>
                Sign In
              </button>
              <button disabled style={{
                padding: '9px 22px',
                backgroundColor: 'transparent',
                color: 'var(--f-primary)',
                fontFamily: "'Cinzel', Georgia, serif",
                fontSize: '10px',
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                border: '1px solid var(--f-primary)',
                cursor: 'not-allowed',
                opacity: 0.45,
              }}>
                Create Account
              </button>
            </div>
            <p style={{
              fontFamily: "'Source Serif 4', Georgia, serif",
              fontStyle: 'italic',
              fontSize: '11px',
              color: 'var(--f-text-4)',
              margin: 0,
            }}>
              ⚔ Cloud sync coming in a future update.
            </p>
          </div>
        </div>

        {/* Support */}
        <div
          ref={supportRef}
          style={{
            opacity: 0,
            maxWidth: '480px',
            margin: '0 auto 40px',
            backgroundColor: 'var(--f-elevated)',
            border: '1px solid var(--f-gold)',
            padding: '28px 32px',
            textAlign: 'center',
          }}
        >
          <p style={{
            fontFamily: "'Cinzel', Georgia, serif",
            fontSize: '8px',
            letterSpacing: '0.45em',
            textTransform: 'uppercase',
            color: 'var(--f-text-4)',
            marginBottom: '8px',
          }}>
            Keep the Standard Flying
          </p>
          <h3 style={{
            fontFamily: "'Cinzel', Georgia, serif",
            fontSize: '17px',
            fontWeight: 700,
            color: 'var(--f-text)',
            margin: '0 0 8px',
          }}>
            Support Battle Standard
          </h3>
          <p style={{
            fontFamily: "'Source Serif 4', Georgia, serif",
            fontStyle: 'italic',
            fontSize: '13px',
            color: 'var(--f-text-3)',
            lineHeight: 1.6,
            margin: '0 0 16px',
          }}>
            Battle Standard is free and always will be. If you appreciate the work, the cost
            of a coffee helps cover the hosting and keeps the updates coming.
          </p>
          <KofiButton />
        </div>

        {/* Attribution & legal footer */}
        <div
          ref={footerRef}
          style={{ opacity: 0, maxWidth: '680px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '0' }}
        >
          <div style={{ borderTop: '1px solid var(--f-border)', paddingTop: '24px', marginBottom: '20px' }}>
            <p style={legalHeading}>About Battle Standard</p>
            <p style={legalText}>
              Battle Standard is a free, unofficial fan project created by hobbyists to help players
              learn Warhammer: The Old World, build legal army lists, and get the most out of
              Games Workshop&rsquo;s matched play rules. Our goal is to lower the barrier to entry
              for new players, support the competitive community, and help grow The Old World player base.
            </p>
            <p style={legalText}>
              This project has no commercial relationship with Games Workshop. All intellectual property
              rights in Warhammer: The Old World - including all rules, settings, characters, and
              associated content - belong entirely to Games Workshop Limited. We make no claim to any
              of it. If Games Workshop ever asks us to take this down, we will.
            </p>
            <p style={legalText}>
              No rules text, artwork, or imagery from Games Workshop publications is reproduced.
              Unit statistics are presented as factual game data to assist list-building.
              For the full rules, refer to your copies of Warhammer: The Old World, Forces of Fantasy,
              Ravening Hordes, and the associated Arcane Journals - all available from{' '}
              <a href="https://www.warhammer.com" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--f-gold)', textDecoration: 'none' }}>
                warhammer.com
              </a>
              .
            </p>
            <p style={legalText}>
              Rules references link to the{' '}
              <a href="https://tow.whfb.app" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--f-gold)', textDecoration: 'none' }}>
                Warhammer Fantasy Online Rules Index Project
              </a>
              {' '}(tow.whfb.app). Battle Standard is not affiliated with that project.
              Icons from{' '}
              <a href="https://game-icons.net" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--f-gold)', textDecoration: 'none' }}>
                game-icons.net
              </a>{' '}
              (CC BY 3.0).
            </p>
          </div>

          <div style={{ borderTop: '1px solid var(--f-border)', paddingTop: '20px', marginBottom: '20px' }}>
            <p style={legalHeading}>Credit</p>
            <p style={legalText}>
              This project is completely unofficial and is in no way endorsed by Games Workshop Limited.
              Based on the work of: Bryan Ansell, Alessio Cavatore, Robin Cruddace, Graham Davey,
              Richard Halliwell, Andy Hoare, Jervis Johnson, Phil Kelly, Andrew Kenrick, Tuomas Pirinen,
              Rick Priestley, Nigel Stillman, Gavin Thorpe, Anthony Reynolds, Jake Thornton,
              Jeremy Vetock, Matthew Ward &amp; Mathias Eliasson.
            </p>
          </div>

          <div style={{ borderTop: '1px solid var(--f-border)', paddingTop: '20px', paddingBottom: '48px' }}>
            <p style={legalHeading}>Trademarks</p>
            <p style={legalText}>
              GW, Games Workshop, Citadel, Warhammer, Warhammer: The Old World, and all associated
              logos, illustrations, images, names, races, characters, and the distinctive likenesses
              thereof, are either &reg;, TM and/or &copy; Copyright Games Workshop Limited
              2000&ndash;2026, variably registered around the world. Used without permission.
              No challenge to their status intended. All Rights Reserved to their respective owners.
            </p>
          </div>
        </div>

      </div>
    </div>
  );
}
